/**
 * PublicSnapshotView — page de consultation publique d'un snapshot via lien
 * de partage (slug + token), hors auth. Charge le détail via getPublicSnapshot
 * et rend le header + les pages figées via SnapshotRenderer. 404 soignée si
 * le lien est invalide ou révoqué.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Link2Off } from 'lucide-react'
import { getPublicSnapshot, downloadPublicSnapshotPdf } from '../features/snapshots/snapshotsApi'
import { SnapshotHeader } from '../features/snapshots/SnapshotHeader'
import { SnapshotRenderer } from '../features/snapshots/SnapshotRenderer'
import type { SnapshotDetail } from '../types/snapshot'

export default function PublicSnapshotView() {
  const { slug, token } = useParams<{ slug: string; token: string }>()
  const [detail, setDetail] = useState<SnapshotDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug || !token) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getPublicSnapshot(slug, token)
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch(() => {
        if (!cancelled) setError('Lien invalide ou révoqué')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug, token])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: '#F4F5FA' }}>
        <p className="text-sm" style={{ color: '#8B8FA7' }}>
          Chargement…
        </p>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center px-6"
        style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #EEF2FF 0%, #F4F5FA 45%, #F4F5FA 100%)' }}
      >
        <div className="w-full max-w-md rounded-3xl border border-[#EEF0F6] bg-white px-8 py-10 text-center shadow-[0_24px_70px_rgba(27,37,89,0.12)]">
          <img src="/logo-orion.png" alt="Orion SEO" className="mx-auto h-9 w-auto" />
          <div className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EEF2FF]">
            <Link2Off size={28} strokeWidth={1.75} className="text-[#6366F1]" />
          </div>
          <h1 className="mt-6 text-xl font-semibold text-[#1B2559]">Ce lien n'est plus valide</h1>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-[#8B8FA7]">
            Ce rapport a été révoqué ou n'existe plus. Contacte ton agence pour obtenir un nouveau lien de partage.
          </p>
        </div>
        <p className="mt-6 text-xs text-[#B0B4C4]">Rapport propulsé par Orion</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F5FA' }}>
      <div className="snapshot-noprint">
        <SnapshotHeader
          reportName={detail.report.name}
          clientName={detail.client.name}
          periodStart={detail.snapshot.periodStart}
          periodEnd={detail.snapshot.periodEnd}
        />
        <div className="flex justify-end gap-2 px-6 pt-4">
          <button
            type="button"
            onClick={() => downloadPublicSnapshotPdf(slug!, token!)}
            className="rounded-lg border border-[#D9DBE9] bg-white px-4 py-2 text-sm font-medium text-[#1B2559] hover:bg-[#F4F5FA]"
          >
            Télécharger PDF
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-[#4F46E5]"
          >
            Imprimer / PDF
          </button>
        </div>
      </div>
      <div className="snapshot-canvas flex justify-center px-6 py-8">
        <SnapshotRenderer pagesFrozen={detail.snapshot.pagesFrozen} dataFrozen={detail.snapshot.dataFrozen} />
      </div>
    </div>
  )
}
